import { useEffect, useState } from "react";
import { Edit2, ExternalLink, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
  FIELD_CLASS, TextField, TextAreaField, ImageUploadField, HeadingItemsField, HeadingGroupsField, HeadingStepsField, HeadingTextField, FaqsField, SlugsField, RichTextField,
} from "@/components/admin/ContentFormFields";

const BLOG_SECTIONS = [
  { key: "advantages", label: "Advantages", kind: "items" },
  { key: "audience", label: "Audience", kind: "items" },
  { key: "eligibility", label: "Eligibility", kind: "items" },
  { key: "documents", label: "Documents Required", kind: "groups" },
  { key: "process", label: "Process", kind: "steps" },
  { key: "timeline", label: "Timeline", kind: "text", textKey: "body" },
  { key: "fees", label: "Fees", kind: "text", textKey: "note" },
  { key: "compliance", label: "Compliance", kind: "items" },
  { key: "whyUs", label: "Why Choose Us", kind: "items" },
];

const SERVICE_SECTIONS = [
  { key: "whoNeedsIt", label: "Who Needs This", kind: "items" },
  { key: "eligibility", label: "Eligibility", kind: "items" },
  { key: "documents", label: "Documents Required", kind: "groups" },
  { key: "process", label: "Process", kind: "steps" },
  { key: "timeline", label: "Timeline", kind: "text", textKey: "body" },
  { key: "fees", label: "Fees", kind: "text", textKey: "note" },
  { key: "benefits", label: "Benefits", kind: "items" },
  { key: "afterService", label: "After Service / Ongoing (optional)", kind: "items" },
  { key: "whyUs", label: "Why Choose Us", kind: "items" },
];

const defaultSectionValue = (kind, textKey) => {
  if (kind === "items") return { heading: "", items: [] };
  if (kind === "groups") return { heading: "", groups: [] };
  if (kind === "steps") return { heading: "", steps: [] };
  if (kind === "text") return { heading: "", [textKey]: "" };
  return null;
};

// Blog posts come in two shapes. "structured" is the original fixed-section
// layout; "rich" is a single WYSIWYG body below the intro. The format is stored
// in sections.format, so posts written before this existed read as structured.
const BLOG_FORMATS = [
  { value: "structured", label: "Structured sections" },
  { value: "rich", label: "Rich text body" },
];

const emptyForm = (isService) => ({
  slug: "",
  category: isService ? "Registration" : "",
  subCategory: "",
  icon: "FileText",
  title: "",
  subtitle: "",
  readTime: "",
  image: "",
  intro: "",
  sections: Object.fromEntries(
    (isService ? SERVICE_SECTIONS : BLOG_SECTIONS).map((s) => [s.key, defaultSectionValue(s.kind, s.textKey)])
  ),
  faqs: [],
  related: [],
  extra: null,
  format: "structured",
  body: "",
});

const rowToForm = (row, isService) => ({
  id: row.id,
  slug: row.slug,
  category: row.category,
  subCategory: row.sub_category || "",
  icon: row.icon || "FileText",
  title: row.title,
  subtitle: row.subtitle,
  readTime: row.read_time,
  image: row.image,
  intro: row.intro,
  sections: Object.fromEntries(
    (isService ? SERVICE_SECTIONS : BLOG_SECTIONS).map((s) => [s.key, row.sections?.[s.key] ?? defaultSectionValue(s.kind, s.textKey)])
  ),
  faqs: row.sections?.faqs || [],
  related: row.sections?.related || [],
  extra: row.sections?.extra ?? null,
  format: row.sections?.format === "rich" ? "rich" : "structured",
  body: row.sections?.body || "",
});

const CATEGORY_PARENT_PATH = { "GST Services": "/taxation/gst", "Income Tax": "/taxation/income-tax", Registration: "/registration", Compliance: "/compliance" };

const publicHref = (row, isService) => (isService ? `${CATEGORY_PARENT_PATH[row.category] || "/registration"}/${row.slug}` : `/blogs/${row.slug}`);

const SectionField = ({ schema, value, onChange }) => {
  if (schema.kind === "items") return <HeadingItemsField label={schema.label} value={value} onChange={onChange} />;
  if (schema.kind === "groups") return <HeadingGroupsField label={schema.label} value={value} onChange={onChange} />;
  if (schema.kind === "steps") return <HeadingStepsField label={schema.label} value={value} onChange={onChange} />;
  if (schema.kind === "text") return <HeadingTextField label={schema.label} value={value} onChange={onChange} textKey={schema.textKey} />;
  return null;
};

const ContentForm = ({ isService, initial, initialReadOnly = false, onCancel, onSave, saving, token }) => {
  const [form, setForm] = useState(initial);
  const [readOnly, setReadOnly] = useState(initialReadOnly);
  const sectionSchema = isService ? SERVICE_SECTIONS : BLOG_SECTIONS;
  const isEdit = Boolean(form.id);
  const isRich = !isService && form.format === "rich";

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setSection = (key, value) => setForm((prev) => ({ ...prev, sections: { ...prev.sections, [key]: value } }));

  const submit = () => {
    const payload = {
      slug: form.slug,
      category: form.category,
      subCategory: isService ? form.subCategory : undefined,
      icon: isService ? form.icon : undefined,
      title: form.title,
      subtitle: form.subtitle,
      readTime: form.readTime,
      image: form.image,
      intro: form.intro,
      // Both shapes are always saved and `format` picks which one the public
      // page renders, so an admin can switch a post between the two without
      // losing whichever body they are not currently using.
      sections: {
        ...form.sections,
        faqs: form.faqs,
        related: form.related,
        ...(isService ? { extra: form.extra } : { format: form.format, body: form.body }),
      },
    };
    onSave(payload, form.id);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="content-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-navy-900">
          {isEdit ? (readOnly ? "View" : "Edit") : "New"} {isService ? "Service" : "Blog Post"}
        </h3>
        {readOnly && (
          <span className="rounded-full bg-[rgb(238,242,250)] px-3 py-1 text-xs font-semibold text-slate-500">Read-only — click Edit to make changes</span>
        )}
      </div>

      <fieldset disabled={readOnly} className={readOnly ? "opacity-70" : ""}>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Slug (lowercase, hyphenated)" value={form.slug} onChange={(v) => setField("slug", v)} placeholder="e.g. gst-registration" />
          <TextField label="Category" value={form.category} onChange={(v) => setField("category", v)} placeholder={isService ? "GST Services / Income Tax / Registration" : "e.g. Company Registration"} />
          {isService && <TextField label="Sub-category (optional)" value={form.subCategory} onChange={(v) => setField("subCategory", v)} placeholder="e.g. Returns & Compliance" />}
          {isService && <TextField label="Icon (lucide-react name)" value={form.icon} onChange={(v) => setField("icon", v)} placeholder="e.g. Receipt" />}
          <TextField label="Read Time" value={form.readTime} onChange={(v) => setField("readTime", v)} placeholder="e.g. 8 min read" />
        </div>

        {!isService && (
          <div className="mt-4">
            <p className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Format</p>
            <div className="flex flex-wrap gap-2" data-testid="blog-format-toggle">
              {BLOG_FORMATS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setField("format", f.value)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                    form.format === f.value
                      ? "bg-navy-900 text-white"
                      : "border border-navy-900/15 text-navy-800 hover:bg-[rgb(238,242,250)]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <ImageUploadField label="Image" value={form.image} onChange={(v) => setField("image", v)} token={token} />
        </div>

        <div className="mt-4">
          <TextField label="Title" value={form.title} onChange={(v) => setField("title", v)} />
        </div>
        <div className="mt-4">
          <TextField label="Subtitle" value={form.subtitle} onChange={(v) => setField("subtitle", v)} />
        </div>
        <div className="mt-4">
          <TextAreaField label="Intro" value={form.intro} onChange={(v) => setField("intro", v)} rows={5} />
        </div>

        {isRich ? (
          <div className="mt-6 space-y-4">
            <RichTextField label="Body" value={form.body} onChange={(v) => setField("body", v)} />
            <SlugsField label="Related (comma-separated slugs)" value={form.related} onChange={(v) => setField("related", v)} />
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {sectionSchema.map((s) => (
              <SectionField key={s.key} schema={s} value={form.sections[s.key]} onChange={(v) => setSection(s.key, v)} />
            ))}
            <FaqsField label="FAQs" value={form.faqs} onChange={(v) => setField("faqs", v)} />
            <SlugsField label="Related (comma-separated slugs)" value={form.related} onChange={(v) => setField("related", v)} />
          </div>
        )}
      </fieldset>

      <div className="mt-6 flex gap-3">
        {readOnly ? (
          <>
            <button
              type="button"
              onClick={() => setReadOnly(false)}
              data-testid="content-form-edit"
              className="inline-flex items-center gap-2 rounded-full bg-emerald2-600 px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02]"
            >
              Edit
            </button>
            <button type="button" onClick={onCancel} className="rounded-full border border-navy-900/15 px-6 py-2.5 text-sm font-semibold text-navy-800">
              Close
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              data-testid="content-form-save"
              className="inline-flex items-center gap-2 rounded-full bg-gold-500 px-6 py-2.5 text-sm font-semibold text-navy-950 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button type="button" onClick={onCancel} className="rounded-full border border-navy-900/15 px-6 py-2.5 text-sm font-semibold text-navy-800">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const ContentTypeSection = ({ isService, token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [viewOnly, setViewOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  const list = isService ? api.listServices : api.listBlogs;
  const create = isService ? api.createService : api.createBlog;
  const update = isService ? api.updateService : api.updateBlog;
  const del = isService ? api.deleteService : api.deleteBlog;

  const load = () => list().then((res) => setItems(isService ? res.services : res.posts));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) await update(token, id, payload);
      else await create(token, payload);
      toast.success(`${isService ? "Service" : "Blog post"} ${id ? "updated" : "created"}.`);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    try {
      await del(token, row.id);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (editing) {
    return (
      <ContentForm
        isService={isService}
        initial={editing === "new" ? emptyForm(isService) : rowToForm(editing, isService)}
        initialReadOnly={viewOnly}
        onCancel={() => {
          setEditing(null);
          setViewOnly(false);
        }}
        onSave={handleSave}
        saving={saving}
        token={token}
      />
    );
  }

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid={`content-new-${isService ? "service" : "blog"}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-4 py-2 text-sm font-semibold text-gold-400 transition-transform duration-300 hover:scale-[1.02]"
        >
          <Plus className="h-4 w-4" /> New {isService ? "Service" : "Blog Post"}
        </button>
      </div>
      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid={`content-table-${isService ? "service" : "blog"}`}>
          <table className="w-full text-left text-sm">
            <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Slug</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id} data-testid={`content-row-${row.slug}`}>
                  <td className="px-5 py-3.5 font-medium text-navy-800">{row.title}</td>
                  <td className="px-5 py-3.5 text-slate-600">{row.category}</td>
                  <td className="px-5 py-3.5 text-slate-500">{row.slug}</td>
                  <td className="px-5 py-3.5 text-slate-500">{new Date(row.updated_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setViewOnly(true);
                          setEditing(row);
                        }}
                        data-testid={`content-view-${row.slug}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-navy-700 hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> View
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setViewOnly(false);
                          setEditing(row);
                        }}
                        data-testid={`content-edit-${row.slug}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald2-600 hover:underline"
                      >
                        <Edit2 className="h-3.5 w-3.5" /> Edit
                      </button>
                      <a
                        href={publicHref(row, isService)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`content-live-${row.slug}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:underline"
                        title="Open live page"
                      >
                        <ExternalLink className="h-3 w-3" /> Live
                      </a>
                      <button type="button" onClick={() => handleDelete(row)} data-testid={`content-delete-${row.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                    No {isService ? "services" : "blog posts"} yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const emptyUpdateForm = { tag: "", text: "", sortOrder: 0 };

const UpdatesSection = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyUpdateForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.listUpdates().then((res) => setItems(res.updates));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return;
    setSaving(true);
    try {
      if (editingId) await api.updateUpdate(token, editingId, form);
      else await api.createUpdate(token, form);
      toast.success(`Update ${editingId ? "saved" : "added"}.`);
      setForm(emptyUpdateForm);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({ tag: row.tag, text: row.text, sortOrder: row.sort_order });
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this update?")) return;
    try {
      await api.deleteUpdate(token, row.id);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="admin-updates-section">
      <form onSubmit={submit} className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-5 sm:grid-cols-[160px_1fr_90px_auto]" data-testid="update-form">
        <TextField label="Tag (optional)" value={form.tag} onChange={(v) => setForm((p) => ({ ...p, tag: v }))} placeholder="New / Alert" />
        <TextField label="Text" value={form.text} onChange={(v) => setForm((p) => ({ ...p, text: v }))} placeholder="Update headline..." />
        <TextField label="Order" value={String(form.sortOrder)} onChange={(v) => setForm((p) => ({ ...p, sortOrder: Number(v) || 0 }))} />
        <div className="flex items-end gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="update-form-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-gold-500 px-4 py-2.5 text-sm font-semibold text-navy-950 disabled:opacity-60"
          >
            {editingId ? "Save" : <><Plus className="h-4 w-4" /> Add</>}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyUpdateForm);
              }}
              className="rounded-full border border-navy-900/15 px-4 py-2.5 text-sm font-semibold text-navy-800"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="updates-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Tag</th>
                <th className="px-5 py-3">Text</th>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id} data-testid={`update-row-${row.id}`}>
                  <td className="px-5 py-3.5 font-medium text-navy-800">{row.tag || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600">{row.text}</td>
                  <td className="px-5 py-3.5 text-slate-500">{row.sort_order}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => startEdit(row)} data-testid={`update-edit-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald2-600 hover:underline">
                        <Edit2 className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(row)} data-testid={`update-delete-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-slate-500">
                    No updates yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RATINGS = [5, 4, 3, 2, 1];

const emptyTestimonialForm = { name: "", role: "", quote: "", image: "", rating: 5, sortOrder: 0 };

const TestimonialsSection = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyTestimonialForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.listTestimonials({ raw: true }).then((res) => setItems(res.testimonials || []));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) return;
    setSaving(true);
    try {
      if (editingId) await api.updateTestimonial(token, editingId, form);
      else await api.createTestimonial(token, form);
      toast.success(`Testimonial ${editingId ? "saved" : "added"}.`);
      setForm(emptyTestimonialForm);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({ name: row.name, role: row.role, quote: row.quote, image: row.image, rating: row.rating || 5, sortOrder: row.sort_order });
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete the testimonial from "${row.name}"?`)) return;
    try {
      await api.deleteTestimonial(token, row.id);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="admin-testimonials-section">
      <form onSubmit={submit} className="mb-6 grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-[rgb(238,242,250)]/60 p-5" data-testid="testimonial-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Name" value={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} placeholder="Ananya Sharma" />
          <TextField label="Role / Company" value={form.role} onChange={(v) => setForm((p) => ({ ...p, role: v }))} placeholder="Founder, Nimbus Labs" />
        </div>
        <TextAreaField label="Quote" value={form.quote} onChange={(v) => setForm((p) => ({ ...p, quote: v }))} rows={3} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_140px_140px]">
          <ImageUploadField label="Photo" value={form.image} onChange={(v) => setForm((p) => ({ ...p, image: v }))} token={token} />
          <div>
            <p className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Rating</p>
            <select
              value={form.rating}
              onChange={(e) => setForm((p) => ({ ...p, rating: Number(e.target.value) }))}
              data-testid="testimonial-form-rating"
              className={FIELD_CLASS}
            >
              {RATINGS.map((r) => (
                <option key={r} value={r}>
                  {"★".repeat(r)} ({r})
                </option>
              ))}
            </select>
          </div>
          <TextField label="Order" value={String(form.sortOrder)} onChange={(v) => setForm((p) => ({ ...p, sortOrder: Number(v) || 0 }))} />
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="testimonial-form-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-gold-500 px-5 py-2.5 text-sm font-semibold text-navy-950 disabled:opacity-60"
          >
            {editingId ? "Save" : <><Plus className="h-4 w-4" /> Add</>}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyTestimonialForm);
              }}
              className="rounded-full border border-navy-900/15 px-5 py-2.5 text-sm font-semibold text-navy-800"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="testimonials-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Quote</th>
                <th className="px-5 py-3">Rating</th>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id} data-testid={`testimonial-row-${row.id}`}>
                  <td className="px-5 py-3.5 font-medium text-navy-800">{row.name}</td>
                  <td className="px-5 py-3.5 text-slate-600">{row.role}</td>
                  <td className="max-w-xs truncate px-5 py-3.5 text-slate-500">{row.quote}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-gold-600">{"★".repeat(row.rating || 5)}</td>
                  <td className="px-5 py-3.5 text-slate-500">{row.sort_order}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => startEdit(row)} data-testid={`testimonial-edit-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald2-600 hover:underline">
                        <Edit2 className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(row)} data-testid={`testimonial-delete-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                    No testimonials yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const emptyPricingForm = { icon: "FileText", title: "", badge: "", rating: 4.8, price: "", priceNote: "onwards", features: [], slug: "", sortOrder: 0 };

const PricingSection = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm] = useState(emptyPricingForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.listPricing().then((res) => setItems(res.plans));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.price.trim()) return;
    setSaving(true);
    try {
      if (editing && editing !== "new") await api.updatePricing(token, editing.id, form);
      else await api.createPricing(token, form);
      toast.success(`Pricing plan ${editing && editing !== "new" ? "saved" : "created"}.`);
      setEditing(null);
      setForm(emptyPricingForm);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      icon: row.icon,
      title: row.title,
      badge: row.badge || "",
      rating: row.rating,
      price: row.price,
      priceNote: row.price_note,
      features: row.features || [],
      slug: row.slug || "",
      sortOrder: row.sort_order,
    });
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete the "${row.title}" pricing plan?`)) return;
    try {
      await api.deletePricing(token, row.id);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (editing) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pricing-form">
        <h3 className="text-lg font-bold text-navy-900">{editing === "new" ? "New" : "Edit"} Pricing Plan</h3>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="Title" value={form.title} onChange={(v) => setField("title", v)} placeholder="GST Registration" />
            <TextField label="Icon (lucide-react name)" value={form.icon} onChange={(v) => setField("icon", v)} placeholder="Receipt" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="Badge (optional)" value={form.badge} onChange={(v) => setField("badge", v)} placeholder="Most Popular" />
            <TextField label="Rating (out of 5)" value={String(form.rating)} onChange={(v) => setField("rating", Number(v) || 0)} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="Price (₹)" value={form.price} onChange={(v) => setField("price", v)} placeholder="999" />
            <TextField label="Price note" value={form.priceNote} onChange={(v) => setField("priceNote", v)} placeholder="onwards / per month" />
          </div>
          <SlugsField label="Features (comma-separated)" value={form.features} onChange={(v) => setField("features", v)} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="Linked service slug (optional)" value={form.slug} onChange={(v) => setField("slug", v)} placeholder="e.g. private-limited-company" />
            <TextField label="Order" value={String(form.sortOrder)} onChange={(v) => setField("sortOrder", Number(v) || 0)} />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="rounded-full bg-gold-500 px-6 py-2.5 text-sm font-semibold text-navy-950 disabled:opacity-60">
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(emptyPricingForm);
              }}
              className="rounded-full border border-navy-900/15 px-6 py-2.5 text-sm font-semibold text-navy-800"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-pricing-section">
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="pricing-new"
          className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-4 py-2 text-sm font-semibold text-gold-400 transition-transform duration-300 hover:scale-[1.02]"
        >
          <Plus className="h-4 w-4" /> New Plan
        </button>
      </div>
      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="pricing-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-[rgb(238,242,250)] text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Badge</th>
                <th className="px-5 py-3">Price</th>
                <th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id} data-testid={`pricing-row-${row.id}`}>
                  <td className="px-5 py-3.5 font-medium text-navy-800">{row.title}</td>
                  <td className="px-5 py-3.5 text-slate-600">{row.badge || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600">₹{row.price} {row.price_note}</td>
                  <td className="px-5 py-3.5 text-slate-500">{row.sort_order}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => startEdit(row)} data-testid={`pricing-edit-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald2-600 hover:underline">
                        <Edit2 className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(row)} data-testid={`pricing-delete-${row.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:underline">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                    No pricing plans yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AdminContent() {
  const { token } = useAuth();
  const [type, setType] = useState("services");

  return (
    <div data-testid="admin-content-page">
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: "services", label: "Services" },
          { id: "blogs", label: "Blog Posts" },
          { id: "updates", label: "Live Updates" },
          { id: "testimonials", label: "Testimonials" },
          { id: "pricing", label: "Pricing" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setType(t.id)}
            data-testid={`content-type-${t.id}`}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
              type === t.id ? "border-gold-500 text-navy-900" : "border-transparent text-slate-500 hover:text-navy-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {type === "updates" ? (
          <UpdatesSection token={token} />
        ) : type === "testimonials" ? (
          <TestimonialsSection token={token} />
        ) : type === "pricing" ? (
          <PricingSection token={token} />
        ) : (
          <ContentTypeSection key={type} isService={type === "services"} token={token} />
        )}
      </div>
    </div>
  );
}
